// The storefront's own pages — categories, sub-categories, brands — for the
// landing-page copy: the link picker lists them, and the template links a
// page to the pages under it. Read once per language from the public list
// the storefront itself uses, in that language's names.
import axiosInstance from "../../../redux/api";

export const PAGE_GROUPS = [
  ["category", "Categories"],
  ["subcategory", "Sub-categories"],
  ["brand", "Brands"],
];

const loaded = {};

/** All published pages in `lang`, cached for the session; a failed read is retried next time. */
export const loadStorefrontPages = (lang) => {
  if (!loaded[lang]) {
    loaded[lang] = axiosInstance
      .get("landing-pages/", { headers: { "Accept-Language": lang } })
      .then((response) =>
        (response?.data?.response?.landing_pages || []).map((page) => ({
          name: page.name,
          path: page.path,
          type: page.page_type,
          parent: page.parent_path,
          filters: page.filters || {},
        })),
      )
      .catch((error) => {
        delete loaded[lang];
        throw error;
      });
  }
  return loaded[lang];
};

/** The address the storefront serves a page at, in one language. */
export const pageHref = (lang, path) => `/${lang}/${path}`;

export const childrenOf = (pages, path) => pages.filter((page) => page.parent === path);

/** Pages whose name or address contains `query`, the page itself left out. */
export const searchPages = (pages, query, currentPath) => {
  const needle = (query || "").trim().toLowerCase();
  return pages.filter(
    (page) =>
      page.path !== currentPath &&
      (!needle || page.name.toLowerCase().includes(needle) || page.path.includes(needle)),
  );
};

/**
 * A link the backend will keep: a path on this site, or a full address on
 * nysmah.com. Anything else is dropped on save, so it is refused here.
 */
export const isStorefrontHref = (href) => {
  const value = (href || "").trim();
  if (!value || /[\s\\]/.test(value)) return false;
  if (value.startsWith("/")) return !value.startsWith("//");
  return /^https:\/\/(www\.)?nysmah\.com(\/|\?|#|$)/.test(value);
};

/** Quill's idea of an empty box is `<p><br></p>`; so is a run of spaces. */
export const isBlankHtml = (html) =>
  !(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

/**
 * The listing the storefront shows for a page's filters — its products,
 * its count, its brand and type facets — read once per language and kept
 * for the session. The draft reads facts from it; the page preview reads
 * the first cards.
 */
const listings = {};

const listingQuery = (filters = {}) => {
  const params = new URLSearchParams();
  for (const key of ["category_ids", "subcategory_ids", "brand_ids"]) {
    if (filters[key]?.length) params.set(key, filters[key].join(","));
  }
  return params.toString();
};

export const loadListing = (lang, filters) => {
  const query = listingQuery(filters);
  if (!query) return Promise.resolve(null);
  const key = `${lang}:${query}`;
  if (!listings[key]) {
    listings[key] = axiosInstance
      .get(`products/?${query}`, { headers: { "Accept-Language": lang } })
      .then((response) => response?.data?.response || {})
      .catch((error) => {
        delete listings[key];
        throw error;
      });
  }
  return listings[key];
};

/** How many products a page lists, the brands with the most of them, and its product types. */
export const loadPageFacts = async (lang, filters) => {
  const body = await loadListing(lang, filters);
  if (!body) return { count: 0, brands: [], types: [] };
  const brands = [...(body.facets?.brands || [])]
    .filter((brand) => brand.count > 0)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .map((brand) => brand.name);
  const types = (body.facets?.types || []).map((type) => type.name).filter(Boolean);
  return { count: Number(body.total_count) || 0, brands, types };
};

/** A product row as the storefront's card shows it: brand on its own line, the name without it. */
const productCard = (row, lang) => {
  const brand = row.brands?.[0]?.name || "";
  const raw = (lang === "ar" && row.ar_title) || row.title || row.product_name || "";
  const title =
    brand && raw.toLowerCase().startsWith(brand.toLowerCase())
      ? raw.slice(brand.length).replace(/^[\s:–-]+/, "")
      : raw;
  const regular = Number(row.regular_price) || 0;
  const price = Number(row.final_price ?? row.sale_price) || regular;
  return {
    id: row.id,
    image: row.image || null,
    brand,
    title,
    price,
    was: price < regular ? regular : null,
    soldOut: Boolean(row.sold_out),
  };
};

/** The first cards of a page's listing, for the preview. */
export const loadPageProducts = async (lang, filters, limit = 6) => {
  const body = await loadListing(lang, filters);
  return (body?.products || []).slice(0, limit).map((row) => productCard(row, lang));
};

const escapeHtml = (text) =>
  String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const link = (lang, page) =>
  `<a href="${pageHref(lang, page.path)}"><strong>${escapeHtml(page.name)}</strong></a>`;

/** "A, B and C" / "A وB وC" from up to three ready-to-print pieces. */
const joinNames = (lang, pieces) => {
  const shown = pieces.slice(0, 3);
  if (lang === "ar") return shown.join(" و");
  return shown.length > 1 ? `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}` : shown[0];
};

const listNames = (lang, names) => joinNames(lang, names.map(escapeHtml));


/** "370 products" / "370 منتجًا" with Arabic's count grammar. */
const products = (lang, count) => {
  if (lang === "ar") {
    if (count === 1) return "منتج واحد";
    if (count === 2) return "منتجان";
    return `${count} ${count <= 10 ? "منتجات" : "منتجًا"}`;
  }
  return `${count} product${count === 1 ? "" : "s"}`;
};

/** "Face Care" → "face care" mid-sentence; a word in capitals (SPF) keeps them. */
const lower = (name) => name.replace(/\b[A-Z][a-z]/g, (word) => word.toLowerCase());

/** "41 original Dior products" / "41 منتجًا أصليًا من Dior" with each language's count grammar. */
const originalProducts = (lang, count, name) => {
  if (lang === "ar") {
    if (count === 1) return `منتجًا أصليًا واحدًا من ${name}`;
    if (count === 2) return `منتجين أصليين من ${name}`;
    return `${count} ${count <= 10 ? "منتجات أصلية" : "منتجًا أصليًا"} من ${name}`;
  }
  return `${count} original ${name} product${count === 1 ? "" : "s"}`;
};

const WRITING = {
  en: {
    heading: (name, kind) =>
      kind === "brand" ? `${name} in Saudi Arabia – Original ${name} Products` : `${name} in Saudi Arabia`,
    opening: ({ kind, name, count, brands, types }) => {
      const what =
        kind === "brand"
          ? count
            ? originalProducts("en", count, escapeHtml(name))
            : `the full range of original ${escapeHtml(name)} products`
          : `${count ? products("en", count) : "original products"} in ${escapeHtml(lower(name))}`;
      const from = brands.length ? `, from ${listNames("en", brands)}` : "";
      const as = types.length ? ` Available as ${listNames("en", types.map(lower))}.` : "";
      return `Discover ${what} at Nysmah${from}.${as} Every item is 100% original and delivered across Saudi Arabia, with free delivery on orders over 300 SAR.`;
    },
    startWith: "Start with the part you’re shopping for:",
    nearby: "Looking for more? These pages are close by:",
    childLine: ({ count, brands }) =>
      count
        ? ` – ${products("en", count)}${brands.length ? ` from ${listNames("en", brands)}` : ""}.`
        : "",
    parentLine: (parent) => ` – browse the whole ${escapeHtml(lower(parent.name))} range.`,
    allBrands: { name: "All brands", path: "brands", line: " – every brand we carry, A to Z." },
    whyTitle: (name, kind) => `Why shop ${kind === "brand" ? escapeHtml(name) : escapeHtml(lower(name))} at Nysmah`,
    why: () =>
      "100% original products, fast delivery to Riyadh, Jeddah, Dammam and every city in Saudi Arabia, easy returns, and payment by mada, Apple Pay, Tabby or Tamara.",
  },
  ar: {
    heading: (name, kind) =>
      kind === "brand" ? `${name} في السعودية – منتجات ${name} الأصلية` : `${name} في السعودية`,
    opening: ({ kind, name, count, brands, types }) => {
      const what =
        kind === "brand"
          ? count
            ? originalProducts("ar", count, escapeHtml(name))
            : `التشكيلة الكاملة من منتجات ${escapeHtml(name)} الأصلية`
          : `${count ? products("ar", count) : "منتجات أصلية"} من قسم ${escapeHtml(name)}`;
      const from = brands.length ? `، من ماركات مثل ${listNames("ar", brands)}` : "";
      const as = types.length ? ` متوفرة بتركيزات ${listNames("ar", types)}.` : "";
      return `اكتشف ${what} في نسمة${from}.${as} كل المنتجات أصلية 100٪ مع توصيل لجميع مناطق المملكة، وشحن مجاني للطلبات فوق 300 ريال.`;
    },
    startWith: "ابدأ من القسم الذي تبحث عنه:",
    nearby: "هل تبحث عن المزيد؟ هذه الصفحات قريبة منك:",
    childLine: ({ count, brands }) =>
      count
        ? ` – ${products("ar", count)}${brands.length ? ` من ${listNames("ar", brands)}` : ""}.`
        : "",
    parentLine: (parent) => ` – تصفّح كل منتجات ${escapeHtml(parent.name)}.`,
    allBrands: { name: "كل الماركات", path: "brands", line: " – جميع الماركات لدينا من الألف إلى الياء." },
    whyTitle: (name) => `لماذا تتسوق ${escapeHtml(name)} من نسمة؟`,
    why: () =>
      "منتجات أصلية 100٪، توصيل سريع إلى الرياض وجدة والدمام وكل مدن المملكة، استرجاع سهل، والدفع عبر مدى وApple Pay وتابي وتمارا.",
  },
};

/**
 * Page copy to publish or polish, in the shape of the pages already live
 * (the Care and Perfumes hubs): a heading naming the page, an opening that
 * says how many original products it lists and from which brands, then —
 * for a hub — a bullet per page under it with its own count and brands;
 * for a leaf — a line back to its parent and sister pages; for a brand —
 * the way to all brands. It closes with why to buy from Nysmah.
 */
export const templateHtml = ({
  lang,
  kind = "category",
  name,
  count = 0,
  brands = [],
  types = [],
  children = [],
  parent = null,
  siblings = [],
}) => {
  const w = WRITING[lang] || WRITING.en;
  const parts = [`<h2>${escapeHtml(w.heading(name, kind))}</h2>`];
  const others = brands.filter((brand) => brand.toLowerCase() !== String(name).toLowerCase());
  const opening = w.opening({ kind, name, count, brands: others, types });
  // Every kind of page lists its links the same way — a bullet per page —
  // the shape the live Care hub is written in.
  let leadIn;
  let bullets;
  if (children.length) {
    leadIn = w.startWith;
    bullets = children.slice(0, 6).map((child) => `${link(lang, child)}${w.childLine(child)}`);
  } else if (parent) {
    leadIn = w.nearby;
    bullets = [
      `${link(lang, parent)}${w.parentLine(parent)}`,
      ...siblings.slice(0, 3).map((page) => `${link(lang, page)}${w.childLine(page)}`),
    ];
  } else {
    leadIn = "";
    bullets = kind === "brand" ? [`${link(lang, w.allBrands)}${w.allBrands.line}`] : [];
  }
  parts.push(`<p>${leadIn ? `${opening} ${leadIn}` : opening}</p>`);
  if (bullets.length) parts.push(`<ul>${bullets.map((item) => `<li>${item}</li>`).join("")}</ul>`);
  parts.push(`<h3>${w.whyTitle(name, kind)}</h3><p>${w.why()}</p>`);
  return parts.join("");
};

/**
 * The draft for one page: its facts and its neighbours' from the catalogue,
 * written up by `templateHtml`. A page not yet saved (no path) is drafted
 * from its name alone; a catalogue that cannot be read costs the numbers,
 * never the draft.
 */
export const draftTemplate = async ({ lang, kind, name, currentPath }) => {
  if (!currentPath) return templateHtml({ lang, kind, name });
  let pages = [];
  try {
    pages = await loadStorefrontPages(lang);
  } catch {
    return templateHtml({ lang, kind, name });
  }
  const page = pages.find((entry) => entry.path === currentPath);
  const children = childrenOf(pages, currentPath).slice(0, 6);
  const parent = page?.parent ? pages.find((entry) => entry.path === page.parent) || null : null;
  const siblings = parent
    ? childrenOf(pages, parent.path).filter((entry) => entry.path !== currentPath)
    : [];
  const safeFacts = (filters) =>
    loadPageFacts(lang, filters).catch(() => ({ count: 0, brands: [], types: [] }));
  const near = siblings.slice(0, 3);
  const [own, ...rest] = await Promise.all([
    safeFacts(page?.filters || {}),
    ...children.map((child) => safeFacts(child.filters)),
    ...near.map((sibling) => safeFacts(sibling.filters)),
  ]);
  return templateHtml({
    lang,
    kind: page?.type || kind,
    name,
    ...own,
    children: children.map((child, index) => ({ ...child, ...rest[index] })),
    parent,
    siblings: near.map((sibling, index) => ({ ...sibling, ...rest[children.length + index] })),
  });
};
